import {
  LifeInsuranceQuoteCard,
  NoQuotesAvailable,
  QuoteErrorDisplay,
  TraditionalLTCQuoteCard,
  type PaymentOption,
  type QuoteWithROI,
  type TraditionalLTCQuoteWithROI,
} from '@/features/shop-insurance';
import { cn } from '@/lib';
import { VALIDATION_ERROR_TYPES } from '@client/util/quoteTypeGuards';

interface ShopInsuranceQuotesListProps {
  filteredAdjustedQuotes: QuoteWithROI[];
  filteredTraditionalLTCQuotesWithEligibility: TraditionalLTCQuoteWithROI[];
  expandedIndices: number[];
  selectedTab: ShopInsuranceTab;
  clientPartner: any;
  premiumValue: number;
  paymentOption: PaymentOption;
  advisorId: string;
  isQuoteSelected: (quote: any) => { isSelected: boolean; fundingSourceId: string | null };
  handleCardToggle: (index: number) => void;
  onClose: () => void;
  knownLimitationError?: QuoteError | null;
  stateOfResidence?: string;
}

export function ShopInsuranceQuotesList({
  filteredAdjustedQuotes,
  filteredTraditionalLTCQuotesWithEligibility,
  expandedIndices,
  selectedTab,
  clientPartner,
  premiumValue,
  paymentOption,
  advisorId,
  isQuoteSelected,
  handleCardToggle,
  onClose,
  knownLimitationError,
  stateOfResidence,
}: ShopInsuranceQuotesListProps) {
  const hasFilteredQuotes =
    filteredAdjustedQuotes.length > 0 || filteredTraditionalLTCQuotesWithEligibility.length > 0;

  // Show specific error displays for validation errors (age, state)
  if (knownLimitationError && VALIDATION_ERROR_TYPES.includes(knownLimitationError.type)) {
    return <QuoteErrorDisplay error={knownLimitationError} />;
  }

  // Show empty state with reason for no quotes (including NO_DEMOGRAPHICS_MATCH, NO_CARRIER_MATCH)
  if (!hasFilteredQuotes) {
    return (
      <NoQuotesAvailable
        advisorId={advisorId}
        reason={knownLimitationError?.type}
      />
    );
  }

  return (
    <div
      className={cn('flex w-full flex-col gap-4')}
      data-testid="shop-insurance-quotes-list"
    >
      {filteredAdjustedQuotes.map((quote: QuoteWithROI, index: number) => {
        const { isSelected, fundingSourceId } = isQuoteSelected(quote);

        return (
          <LifeInsuranceQuoteCard
            key={`${quote.insuranceQuoteId}-${premiumValue}`}
            testId="shop-insurance-quote-card"
            quote={quote}
            isExpanded={expandedIndices.includes(index)}
            onToggleExpand={() => handleCardToggle(index)}
            onClose={onClose}
            hasHighestROI={index === 0}
            isSelected={isSelected}
            fundingSourceId={fundingSourceId}
            selectedTab={selectedTab}
            clientPartner={clientPartner}
          />
        );
      })}

      {filteredTraditionalLTCQuotesWithEligibility.map((quote, index) => {
        const adjustedIndex = filteredAdjustedQuotes.length + index;
        const { isSelected, fundingSourceId } = isQuoteSelected(quote);

        return (
          <TraditionalLTCQuoteCard
            key={`${quote.traditionalLTCQuoteId}-${paymentOption}`}
            testId="shop-insurance-quote-card"
            quote={quote}
            isExpanded={expandedIndices.includes(adjustedIndex)}
            onToggleExpand={() => handleCardToggle(adjustedIndex)}
            onClose={onClose}
            isSelected={isSelected}
            fundingSourceId={fundingSourceId}
            stateOfResidence={stateOfResidence}
          />
        );
      })}
    </div>
  );
}
